import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  AlertCircle,
  BookOpen,
  Download,
  FileText,
  Hash,
  Printer,
  RefreshCw,
  RotateCcw,
  Save
} from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Toast } from './ui/Toast';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { Interrupteur, LigneReglage, Encadre } from './parametrage/PiecesReglage';
import { api } from '../services/api';
import { imprimerSortie, sonderAgent, oublierReglagesImpression, ouvrirTiroirAgent, rapporterSortie } from '../utils/imprimerRecu';
import OngletJournalRecus from './recus/OngletJournalRecus';

/**
 * Paramètres des reçus de caisse.
 *
 * ----------------------------------------------------------------------------
 * Pourquoi cette page
 * ----------------------------------------------------------------------------
 * Depuis le 03/11/2026, chaque encaissement émet un reçu figé et numéroté
 * (RecuCaisseService). Cette page règle comment il sort : sur l'imprimante de
 * tickets 80 mm ou en A4, par le navigateur (voie A) ou par l'agent KLASYS du
 * poste de caisse (voie B, lot 3) — deux voies indépendantes, décision de
 * Daniel —, tout de suite après l'encaissement ou à la demande, en un ou deux
 * exemplaires ; ce qu'il montre ; et comment il est numéroté.
 *
 * Trois onglets, trois sections au régime B (PARAMETRAGE-MODULES.md §5) :
 * chacune s'enregistre séparément. Un quatrième, « Journal » (04/11/2026), lit
 * les ouvertures du tiroir-caisse et les reçus imprimés, et suit le stock de
 * papier thermique (recus/OngletJournalRecus.jsx). L'onglet de numérotation est le seul à
 * toucher au compteur : enregistrer l'impression pendant que la caisse
 * encaisse ne le ramène pas en arrière.
 */

const ACCENT_IMPRESSION = '#246990';
const ACCENT_IMPRESSION_SOMBRE = '#1a4f6e';
const ACCENT_CONTENU = '#319190';
const ACCENT_CONTENU_SOMBRE = '#2a7a79';
const ACCENT_NUMERO = '#ae7467';
const ACCENT_NUMERO_SOMBRE = '#8f5c51';

const BOUTON_REINITIALISER = 'flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';
const boutonEnregistrer = (actif, accent) => ({
  className: `flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${
    actif ? 'hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'
  }`,
  style: actif ? { backgroundColor: accent } : undefined
});

const RappelNonEnregistre = ({ onEnregistrer, saving, accent }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
      <AlertCircle size={15} className="text-amber-600" />
    </div>
    <p className="text-xs font-semibold text-amber-700">Vous avez des modifications non enregistrées.</p>
    <Button
      onClick={onEnregistrer}
      disabled={saving}
      className="ml-auto h-9 min-w-[168px] justify-center gap-2 text-white hover:opacity-90"
      style={{ backgroundColor: accent }}
    >
      <Save className="h-4 w-4" />
      {saving ? 'Enregistrement…' : 'Enregistrer maintenant'}
    </Button>
  </div>
);

/** Un choix parmi deux ou trois, en boutons accolés. */
const Choix = ({ options, valeur, onChoisir, accent, desactive }) => (
  <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
    {options.map((o) => {
      const actif = valeur === o.valeur;
      return (
        <button
          key={String(o.valeur)}
          type="button"
          onClick={() => onChoisir(o.valeur)}
          disabled={desactive}
          aria-pressed={actif}
          className={`h-8 min-w-[104px] rounded-md px-3 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
            actif ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
          }`}
          style={actif ? { backgroundColor: accent } : undefined}
        >
          {o.libelle}
        </button>
      );
    })}
  </div>
);

/** Le brouillon d'une section : les champs qu'elle règle, comparés à l'état enregistré. */
const useBrouillon = (enregistre, cles) => {
  const extraire = useCallback(
    (source) => Object.fromEntries(cles.map((c) => [c, source?.[c]])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cles.join('|')]
  );
  const [brouillon, setBrouillon] = useState(() => extraire(enregistre));
  useEffect(() => { setBrouillon(extraire(enregistre)); }, [enregistre, extraire]);
  const hasChanges = cles.some((c) => brouillon[c] !== enregistre?.[c]);
  const changer = (cle, valeur) => setBrouillon((b) => ({ ...b, [cle]: valeur }));
  const reinitialiser = () => setBrouillon(extraire(enregistre));
  return { brouillon, changer, hasChanges, reinitialiser };
};

const ActionsSection = ({ accent, hasChanges, saving, onReinitialiser, onEnregistrer, onEssai, essaiEnCours }) => {
  const enregistrer = boutonEnregistrer(hasChanges && !saving, accent);
  return (
    <>
      {onEssai && (
        <button type="button" onClick={onEssai} disabled={essaiEnCours} className={BOUTON_REINITIALISER}>
          <Printer size={13} /> {essaiEnCours ? 'Préparation…' : "Ticket d'essai"}
        </button>
      )}
      <button type="button" onClick={onReinitialiser} disabled={saving || !hasChanges} className={BOUTON_REINITIALISER}>
        <RotateCcw size={13} /> Réinitialiser
      </button>
      <button
        type="button"
        onClick={onEnregistrer}
        disabled={saving || !hasChanges}
        className={enregistrer.className}
        style={enregistrer.style}
      >
        {saving
          ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          : <Save size={13} />}
        Enregistrer
      </button>
    </>
  );
};

// ============================================================================
// Onglet Impression
// ============================================================================

const CLES_IMPRESSION = [
  'format', 'navigateur', 'agent', 'agent_port', 'impression_auto', 'exemplaires', 'couper_papier', 'ouvrir_tiroir'
];

/** L'agent de CE poste : ce qu'il répond, ou son absence. */
const EtatAgentDuPoste = ({ port, accent }) => {
  const [etatAgent, setEtatAgent] = useState(undefined);
  const verifier = useCallback(async () => {
    setEtatAgent(undefined);
    setEtatAgent(await sonderAgent(port));
  }, [port]);
  useEffect(() => { verifier(); }, [verifier]);

  let texte;
  if (etatAgent === undefined) texte = 'Recherche de l\'agent sur ce poste…';
  else if (etatAgent === null) texte = `Aucun agent ne répond sur ce poste (port ${port}). Installez-le avec le kit, ou ce poste imprimera par le navigateur.`;
  else if (!etatAgent.imprimante_trouvee) texte = `Agent ${etatAgent.version} trouvé, mais aucune imprimante choisie : ouvrez ses réglages (icône K près de l'horloge).`;
  else texte = `Agent ${etatAgent.version} prêt, imprimante « ${etatAgent.imprimante} »${etatAgent.imprimante_en_ligne ? '' : ' — hors ligne (éteinte ou débranchée)'}.`;

  return (
    <LigneReglage
      actif={Boolean(etatAgent && etatAgent.imprimante_trouvee)}
      accent={accent}
      libelle="Sur ce poste"
      description={texte}
    >
      <button type="button" onClick={verifier} className={BOUTON_REINITIALISER}>
        <RefreshCw size={13} /> Vérifier
      </button>
    </LigneReglage>
  );
};

const SectionImpression = ({ etat, enregistrer, imprimerEssai, avertir }) => {
  const { brouillon, changer, hasChanges, reinitialiser } = useBrouillon(etat.impression, CLES_IMPRESSION);
  const [saving, setSaving] = useState(false);
  const [essai, setEssai] = useState(false);
  const [kit, setKit] = useState(false);
  const [tiroirEnCours, setTiroirEnCours] = useState(false);
  const accent = ACCENT_IMPRESSION;

  // L'essai est noté au journal AVANT que l'agent n'ouvre le tiroir : sans
  // journal, pas d'ouverture (décision de Daniel du 04/11/2026).
  const essayerTiroir = async () => {
    setTiroirEnCours(true);
    let sortieId = null;
    try {
      const note = await api.recusCaisse.essaiTiroir();
      if (!note?.success) {
        avertir(note?.message || "L'essai du tiroir n'a pas pu être journalisé : le tiroir reste fermé.");
        return;
      }
      sortieId = note.data?.sortie_id || null;
      await ouvrirTiroirAgent(Number(brouillon.agent_port) || 4210);
      rapporterSortie(sortieId, { voie: 'agent', tiroir: 'ouvert' });
      avertir("Commande envoyée au tiroir-caisse, et notée au journal. S'il reste fermé : serrure en position automatique, câble RJ11, puis broche ou durée dans les réglages de l'agent.", 'info');
    } catch (e) {
      rapporterSortie(sortieId, { voie: 'aucune', tiroir: 'non_ouvert', message: e.message });
      avertir(e.message);
    } finally {
      setTiroirEnCours(false);
    }
  };

  const basculerAgent = () => {
    const suivant = !brouillon.agent;
    changer('agent', suivant);
    // Sans agent, le navigateur redevient la seule voie : il ne peut pas rester éteint.
    if (!suivant) changer('navigateur', true);
  };

  const telechargerKit = async () => {
    setKit(true);
    const reponse = await api.recusCaisse.telechargerKit();
    setKit(false);
    if (!reponse?.success) {
      avertir(reponse?.message || "Le kit n'a pas pu être téléchargé");
      return;
    }
    const url = window.URL.createObjectURL(reponse.data);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = 'KLASYS-Impression-kit.zip';
    document.body.appendChild(lien);
    lien.click();
    setTimeout(() => { lien.remove(); window.URL.revokeObjectURL(url); }, 200);
  };

  const valider = async () => {
    setSaving(true);
    await enregistrer({ impression: { ...etat.impression, ...brouillon } }, 'Réglages d\'impression enregistrés');
    setSaving(false);
  };
  const lancerEssai = async () => {
    setEssai(true);
    await imprimerEssai({ ...etat.impression, ...brouillon });
    setEssai(false);
  };

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={Printer}
        titre="Impression du reçu"
        sousTitre="Le papier, la voie d'impression, et ce qui se passe à la validation d'un paiement"
        accent={accent}
        accentSombre={ACCENT_IMPRESSION_SOMBRE}
        actions={(
          <ActionsSection
            accent={accent}
            hasChanges={hasChanges}
            saving={saving}
            onReinitialiser={reinitialiser}
            onEnregistrer={valider}
            onEssai={lancerEssai}
            essaiEnCours={essai}
          />
        )}
      />

      <CarteReglages titre="Papier" accent={accent} compteur="1 réglage" dense>
        <LigneReglage
          accent={accent}
          libelle="Format du reçu"
          description="Ticket 80 mm pour l'imprimante thermique de la caisse : il sort à la longueur du reçu, en noir. A4 pour une imprimante de bureau."
        >
          <Choix
            accent={accent}
            valeur={brouillon.format}
            onChoisir={(v) => changer('format', v)}
            options={[{ valeur: 'ticket80', libelle: 'Ticket 80 mm' }, { valeur: 'a4', libelle: 'A4' }]}
          />
        </LigneReglage>
      </CarteReglages>

      <CarteReglages titre="Voies d'impression" accent={accent} compteur="2 voies" dense>
        <LigneReglage
          actif={brouillon.navigateur}
          accent={accent}
          libelle="Par le navigateur"
          description="Le reçu s'ouvre dans la boîte d'impression du navigateur, qui l'envoie au pilote Windows de l'imprimante. Rien à installer de plus."
        >
          <Interrupteur
            actif={brouillon.navigateur}
            accent={accent}
            libelle="Imprimer par le navigateur"
            enCours={saving}
            // Sans agent, le navigateur est la seule voie : il ne s'éteint pas.
            desactive={!brouillon.agent}
            onBasculer={() => changer('navigateur', !brouillon.navigateur)}
          />
        </LigneReglage>
        <LigneReglage
          actif={brouillon.agent}
          accent={accent}
          libelle="Par l'agent KLASYS du poste de caisse"
          description="Un petit logiciel posé sur chaque poste de caisse, qui parle directement à l'imprimante : sans boîte d'impression, papier coupé, tiroir-caisse ouvert. Passe en premier ; un poste sans agent imprime par le navigateur, s'il est activé."
        >
          <Interrupteur
            actif={brouillon.agent}
            accent={accent}
            libelle="Imprimer par l'agent KLASYS"
            enCours={saving}
            onBasculer={basculerAgent}
          />
        </LigneReglage>
      </CarteReglages>

      <CarteReglages titre="Agent KLASYS" accent={accent} compteur="Ticket 80 mm" dense>
        <EtatAgentDuPoste port={Number(brouillon.agent_port) || 4210} accent={accent} />
        <LigneReglage
          accent={accent}
          libelle="Kit d'installation d'un poste"
          description="Un ZIP : le pilote de l'imprimante fourni par l'éditeur, l'agent, et le guide d'installation en PDF. À installer sur chaque poste qui imprime des reçus."
        >
          <button type="button" onClick={telechargerKit} disabled={kit} className={BOUTON_REINITIALISER}>
            <Download size={13} /> {kit ? 'Préparation…' : 'Télécharger le kit'}
          </button>
        </LigneReglage>
        <LigneReglage
          actif={brouillon.agent && brouillon.couper_papier}
          accent={accent}
          libelle="Couper le papier"
          description="L'agent coupe le ticket après chaque exemplaire (coupe partielle de l'imprimante)."
        >
          <Interrupteur
            actif={brouillon.couper_papier}
            accent={accent}
            libelle="Couper le papier"
            enCours={saving}
            desactive={!brouillon.agent}
            onBasculer={() => changer('couper_papier', !brouillon.couper_papier)}
          />
        </LigneReglage>
        <LigneReglage
          actif={brouillon.agent && brouillon.ouvrir_tiroir}
          accent={accent}
          libelle="Ouvrir le tiroir-caisse"
          description="À l'encaissement seulement : première impression d'un reçu payé en espèces, dans les 15 minutes qui suivent le paiement. Jamais pour un duplicata ni pour un reçu ancien réimprimé depuis la liste ; le ticket d'essai l'ouvre aussi tant que l'option est active. Chaque ouverture est notée au journal (onglet Journal). Le tiroir se branche sur la prise RJ11 de l'imprimante, serrure en position automatique. « Essayer » l'ouvre tout de suite ; s'il reste fermé, changez la broche ou la durée dans les réglages de l'agent (icône K)."
        >
          <button type="button" onClick={essayerTiroir} disabled={!brouillon.agent || tiroirEnCours} className={BOUTON_REINITIALISER}>
            {tiroirEnCours ? 'Ouverture…' : 'Essayer'}
          </button>
          <Interrupteur
            actif={brouillon.ouvrir_tiroir}
            accent={accent}
            libelle="Ouvrir le tiroir-caisse"
            enCours={saving}
            desactive={!brouillon.agent}
            onBasculer={() => changer('ouvrir_tiroir', !brouillon.ouvrir_tiroir)}
          />
        </LigneReglage>
        <LigneReglage
          actif={brouillon.agent}
          accent={accent}
          libelle="Port de l'agent"
          description="L'adresse locale où KLASYS parle à l'agent (127.0.0.1). 4210 par défaut ; à changer seulement si un autre logiciel du poste l'occupe."
        >
          <input
            type="number"
            min="1024"
            max="65535"
            value={brouillon.agent_port}
            onChange={(e) => changer('agent_port', Number(e.target.value.replace(/[^\d]/g, '')) || '')}
            disabled={!brouillon.agent}
            className="h-9 w-28 rounded-lg border border-gray-200 text-center text-sm focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15 disabled:bg-gray-50"
          />
        </LigneReglage>
      </CarteReglages>

      <CarteReglages titre="À la validation d'un paiement" accent={accent} compteur="2 réglages" dense>
        <LigneReglage
          actif={brouillon.impression_auto}
          accent={accent}
          libelle="Imprimer tout de suite"
          description="Le reçu part à l'imprimante dès que le paiement est enregistré. Sinon, la caisse l'imprime d'un clic sur « Imprimer le reçu »."
        >
          <Interrupteur
            actif={brouillon.impression_auto}
            accent={accent}
            libelle="Imprimer le reçu dès la validation"
            enCours={saving}
            onBasculer={() => changer('impression_auto', !brouillon.impression_auto)}
          />
        </LigneReglage>
        <LigneReglage
          accent={accent}
          libelle="Exemplaires"
          description="Deux exemplaires : celui du payeur, et la souche que l'école garde. Chacun porte sa mention."
        >
          <Choix
            accent={accent}
            valeur={Number(brouillon.exemplaires)}
            onChoisir={(v) => changer('exemplaires', v)}
            options={[{ valeur: 1, libelle: 'Un' }, { valeur: 2, libelle: 'Deux' }]}
          />
        </LigneReglage>
      </CarteReglages>

      <Encadre titre="Imprimer sans la boîte de dialogue">
        <p>
          Avec l'agent KLASYS, c'est le cas d'office. Par le navigateur seul : faites de l'imprimante de tickets
          l'imprimante par défaut de Windows et lancez Chrome avec l'option
          <span className="font-mono"> --kiosk-printing</span>.
        </p>
        <p className="mt-1">« Ticket d'essai » imprime un reçu fictif avec les réglages à l'écran, sans rien enregistrer.</p>
      </Encadre>

      {hasChanges && <RappelNonEnregistre onEnregistrer={valider} saving={saving} accent={accent} />}
    </div>
  );
};

// ============================================================================
// Onglet Contenu du reçu
// ============================================================================

const CLES_CONTENU = ['afficher_logo', 'afficher_qr', 'afficher_reste', 'message_pied'];

const SectionContenu = ({ etat, enregistrer, imprimerEssai }) => {
  const { brouillon, changer, hasChanges, reinitialiser } = useBrouillon(etat.impression, CLES_CONTENU);
  const [saving, setSaving] = useState(false);
  const [essai, setEssai] = useState(false);
  const accent = ACCENT_CONTENU;

  const valider = async () => {
    setSaving(true);
    await enregistrer(
      { impression: { ...etat.impression, ...brouillon, message_pied: String(brouillon.message_pied || '').trim() } },
      'Contenu du reçu enregistré'
    );
    setSaving(false);
  };
  const lancerEssai = async () => {
    setEssai(true);
    await imprimerEssai({ ...etat.impression, ...brouillon });
    setEssai(false);
  };

  const interrupteur = (cle, libelle, description) => (
    <LigneReglage actif={brouillon[cle]} accent={accent} libelle={libelle} description={description}>
      <Interrupteur
        actif={brouillon[cle]}
        accent={accent}
        libelle={libelle}
        enCours={saving}
        onBasculer={() => changer(cle, !brouillon[cle])}
      />
    </LigneReglage>
  );

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={FileText}
        titre="Contenu du reçu"
        sousTitre="Ce que le reçu montre, en plus du numéro, de la date, du payeur et des montants"
        accent={accent}
        accentSombre={ACCENT_CONTENU_SOMBRE}
        actions={(
          <ActionsSection
            accent={accent}
            hasChanges={hasChanges}
            saving={saving}
            onReinitialiser={reinitialiser}
            onEnregistrer={valider}
            onEssai={lancerEssai}
            essaiEnCours={essai}
          />
        )}
      />

      <CarteReglages titre="Éléments du reçu" accent={accent} compteur="3 réglages" dense>
        {interrupteur('afficher_logo', "Logo de l'école",
          "En tête du reçu, en niveaux de gris sur le ticket. Celui de la fiche de l'établissement.")}
        {interrupteur('afficher_qr', 'QR code de vérification',
          "Un parent ou un contrôleur le scanne pour vérifier que le reçu est authentique et voir s'il a été annulé. Le code est imprimé en clair dessous.")}
        {interrupteur('afficher_reste', 'Reste dû après le paiement',
          "Sous chaque ligne : ce qu'il reste à payer sur le mois juste après ce versement, ou « Soldé ». Figé au jour du paiement.")}
      </CarteReglages>

      <CarteReglages titre="Message de bas de reçu" accent={accent} compteur={`${String(brouillon.message_pied || '').length} / 120`}>
        <div className="space-y-2">
          <p className="text-[11px] leading-relaxed text-gray-400">
            Une ligne imprimée tout en bas : un remerciement, les horaires de la caisse, un rappel. Laissez vide pour ne rien imprimer.
          </p>
          <input
            type="text"
            value={brouillon.message_pied || ''}
            onChange={(e) => changer('message_pied', e.target.value)}
            maxLength={120}
            className="h-9 w-full rounded-lg border border-gray-200 px-3 text-sm focus:border-[#319190] focus:outline-none focus:ring-2 focus:ring-[#319190]/15"
            placeholder="Merci de votre confiance."
          />
        </div>
      </CarteReglages>

      {hasChanges && <RappelNonEnregistre onEnregistrer={valider} saving={saving} accent={accent} />}
    </div>
  );
};

// ============================================================================
// Onglet Numérotation
// ============================================================================

const SectionNumerotation = ({ etat, enregistrer }) => {
  const [modele, setModele] = useState(etat.modele_numero);
  const [prochain, setProchain] = useState(String(etat.prochain_numero || 1));
  const [apercu, setApercu] = useState(etat.apercu);
  const [erreur, setErreur] = useState(null);
  const [calcul, setCalcul] = useState(false);
  const [saving, setSaving] = useState(false);
  const minuteur = useRef(null);
  const accent = ACCENT_NUMERO;

  useEffect(() => {
    setModele(etat.modele_numero);
    setProchain(String(etat.prochain_numero || 1));
    setApercu(etat.apercu);
    setErreur(null);
  }, [etat]);

  const hasChanges = modele.trim() !== etat.modele_numero || prochain !== String(etat.prochain_numero || 1);

  // L'aperçu vient du serveur : lui seul applique la règle du modèle.
  useEffect(() => {
    if (!hasChanges) {
      setApercu(etat.apercu);
      setErreur(null);
      return undefined;
    }
    clearTimeout(minuteur.current);
    minuteur.current = setTimeout(async () => {
      setCalcul(true);
      const reponse = await api.recusCaisse.apercuNumero({ modele_numero: modele.trim(), prochain_numero: Number(prochain) || 0 });
      setCalcul(false);
      if (reponse?.success) {
        setApercu(reponse.data.apercu);
        setErreur(null);
      } else {
        setErreur(reponse?.message || 'Modèle invalide');
      }
    }, 400);
    return () => clearTimeout(minuteur.current);
  }, [modele, prochain, hasChanges, etat.apercu]);

  const reinitialiser = () => {
    setModele(etat.modele_numero);
    setProchain(String(etat.prochain_numero || 1));
  };

  const valider = async () => {
    if (erreur) return;
    setSaving(true);
    await enregistrer({ modele_numero: modele.trim(), prochain_numero: Number(prochain) }, 'Numérotation des reçus enregistrée');
    setSaving(false);
  };

  const enregistrerBouton = boutonEnregistrer(hasChanges && !erreur && !saving, accent);

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={Hash}
        titre="Numérotation des reçus"
        sousTitre="Le modèle du numéro attribué à chaque reçu, et le prochain numéro"
        accent={accent}
        accentSombre={ACCENT_NUMERO_SOMBRE}
        actions={(
          <>
            <button type="button" onClick={reinitialiser} disabled={saving || !hasChanges} className={BOUTON_REINITIALISER}>
              <RotateCcw size={13} /> Réinitialiser
            </button>
            <button
              type="button"
              onClick={valider}
              disabled={saving || !hasChanges || Boolean(erreur)}
              className={enregistrerBouton.className}
              style={enregistrerBouton.style}
            >
              {saving
                ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                : <Save size={13} />}
              Enregistrer
            </button>
          </>
        )}
      />

      <CarteReglages titre="Modèle" accent={accent} compteur="2 réglages" dense>
        <div className="flex flex-wrap items-start justify-between gap-6 px-5 py-4">
          <div className="max-w-xl">
            <p className="text-sm font-semibold text-gray-800">Modèle du numéro</p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
              Le dernier groupe de <span className="font-mono">0</span> porte le numéro et fixe sa largeur :
              <span className="font-mono"> REC-00000</span> donne REC-00001, REC-00002…
              <span className="font-mono"> {'{AAAA}'}</span> ou <span className="font-mono">{'{AA}'}</span> insère
              l'année scolaire ; la numérotation repart alors à 1 chaque année.
            </p>
          </div>
          <div className="w-64 flex-shrink-0">
            <input
              type="text"
              value={modele}
              onChange={(e) => setModele(e.target.value.toUpperCase().replace(/\s/g, ''))}
              maxLength={30}
              spellCheck="false"
              className={`h-9 w-full rounded-lg border px-3 font-mono text-sm focus:outline-none focus:ring-2 ${
                erreur
                  ? 'border-red-300 bg-red-50 text-red-900 focus:ring-red-200'
                  : 'border-gray-200 focus:border-[#ae7467] focus:ring-[#ae7467]/15'
              }`}
            />
            {erreur && <p className="mt-1.5 text-[11px] leading-relaxed text-red-700">{erreur}</p>}
          </div>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-6 px-5 py-4">
          <div className="max-w-xl">
            <p className="text-sm font-semibold text-gray-800">Prochain numéro</p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
              Pour reprendre la suite d'un carnet de reçus papier. Un numéro déjà porté par un reçu est sauté :
              deux reçus ne reçoivent jamais le même.
            </p>
          </div>
          <input
            type="number"
            min="1"
            value={prochain}
            onChange={(e) => setProchain(e.target.value.replace(/[^\d]/g, ''))}
            className="h-9 w-32 flex-shrink-0 rounded-lg border border-gray-200 text-center text-sm focus:border-[#ae7467] focus:outline-none focus:ring-2 focus:ring-[#ae7467]/15"
          />
        </div>
      </CarteReglages>

      <CarteReglages titre="Aperçu" accent={accent} compteur={`Année ${etat.annee}`}>
        {apercu && !erreur ? (
          <p className="text-xs text-gray-500">
            {hasChanges ? 'Le prochain reçu porterait :' : 'Le prochain reçu portera :'}
            <span className="ml-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 font-mono text-sm text-gray-800">{apercu}</span>
            {calcul && <span className="ml-2 text-gray-400">calcul…</span>}
          </p>
        ) : (
          <p className="text-xs text-gray-400">Corrigez le modèle pour voir l'aperçu.</p>
        )}
      </CarteReglages>

      <Encadre titre="Ce qui change, et ce qui ne change pas">
        <p>
          Le numéro est attribué par le serveur au moment où le paiement est enregistré, à la suite, et ne
          change plus : une réimpression porte le même numéro, avec la mention DUPLICATA.
        </p>
        <p className="mt-1">Changer de modèle ne renumérote aucun reçu déjà émis.</p>
      </Encadre>

      {hasChanges && !erreur && <RappelNonEnregistre onEnregistrer={valider} saving={saving} accent={accent} />}
    </div>
  );
};

// ============================================================================
// La page
// ============================================================================

const ParametresRecus = ({ onBackToDashboard }) => {
  const [etat, setEtat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const avertir = useCallback((message, type = 'error') => setToast({ show: true, message, type }), []);

  const charger = useCallback(async () => {
    setLoading(true);
    const reponse = await api.recusCaisse.reglages();
    if (reponse?.success) {
      setEtat(reponse.data);
      setErreur(null);
    } else {
      setErreur(reponse?.message || 'Lecture des réglages impossible');
    }
    setLoading(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const enregistrer = useCallback(async (partie, messageSucces) => {
    const reponse = await api.recusCaisse.enregistrerReglages(partie);
    if (reponse?.success) {
      setEtat(reponse.data);
      // Les écrans de caisse relisent les réglages au prochain reçu.
      oublierReglagesImpression();
      avertir(messageSucces, 'success');
      return true;
    }
    avertir(reponse?.message || "Erreur lors de l'enregistrement");
    return false;
  }, [avertir]);

  // Le ticket d'essai suit les réglages à l'écran : par l'agent s'il est
  // activé (le navigateur en secours), sinon par le navigateur.
  const imprimerEssai = useCallback(async (impression) => {
    const reponse = await api.recusCaisse.ticketTest(impression, { pourAgent: Boolean(impression.agent) });
    if (!reponse?.success || !reponse.data?.html) {
      avertir(reponse?.message || "Le ticket d'essai n'a pas pu être préparé");
      return;
    }
    try {
      const { voie, message } = await imprimerSortie(reponse.data, impression);
      if (message) avertir(message, 'info');
      else if (voie === 'agent') avertir("Ticket d'essai envoyé à l'agent du poste", 'success');
    } catch (e) {
      avertir(e.message || "L'impression n'a pas pu être lancée");
    }
  }, [avertir]);

  // Classes écrites en entier : Tailwind ne génère que ce qu'il lit.
  const onglets = useMemo(() => ([
    { valeur: 'impression', libelle: 'Impression', icone: Printer, actif: 'data-[state=active]:bg-[#246990]' },
    { valeur: 'contenu', libelle: 'Contenu du reçu', icone: FileText, actif: 'data-[state=active]:bg-[#319190]' },
    { valeur: 'numerotation', libelle: 'Numérotation', icone: Hash, actif: 'data-[state=active]:bg-[#ae7467]' },
    { valeur: 'journal', libelle: 'Journal', icone: BookOpen, actif: 'data-[state=active]:bg-[#3084b5]' }
  ]), []);

  return (
    <div className="w-full">
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      )}

      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={Printer}
        titre="Paramètres des reçus de caisse"
        sousTitre="Impression sur ticket ou en A4, contenu du reçu, numérotation, journal du tiroir et du papier"
      />

      {loading && !etat && (
        <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
          <ChargementSection accent={ACCENT_IMPRESSION} libelle="Chargement des réglages des reçus…" />
        </div>
      )}

      {!loading && erreur && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-700" />
          <div className="min-w-0 text-sm text-red-800">
            <p className="font-semibold text-red-900">Les réglages des reçus n'ont pas pu être lus</p>
            <p className="mt-1">{erreur}</p>
            <Button
              variant="outline"
              onClick={charger}
              className="mt-3 h-9 gap-2 border-red-300 text-red-800 hover:bg-red-100"
            >
              <RefreshCw size={15} /> Réessayer
            </Button>
          </div>
        </div>
      )}

      {etat && !erreur && (
        <Tabs defaultValue="impression" className="w-full">
          <TabsList className="mb-6 grid grid-cols-4 rounded-xl bg-[#246990]/8 p-1">
            {onglets.map(({ valeur, libelle, icone: Icone, actif }) => (
              <TabsTrigger
                key={valeur}
                value={valeur}
                className={`rounded-lg text-sm font-semibold text-gray-600 transition-all data-[state=active]:text-white data-[state=active]:shadow-sm ${actif}`}
              >
                <Icone size={15} className="mr-2" />
                <span>{libelle}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
            <TabsContent value="impression" className="mt-0 focus-visible:outline-none">
              <SectionImpression etat={etat} enregistrer={enregistrer} imprimerEssai={imprimerEssai} avertir={avertir} />
            </TabsContent>
            <TabsContent value="contenu" className="mt-0 focus-visible:outline-none">
              <SectionContenu etat={etat} enregistrer={enregistrer} imprimerEssai={imprimerEssai} />
            </TabsContent>
            <TabsContent value="numerotation" className="mt-0 focus-visible:outline-none">
              <SectionNumerotation etat={etat} enregistrer={enregistrer} />
            </TabsContent>
            <TabsContent value="journal" className="mt-0 focus-visible:outline-none">
              <OngletJournalRecus avertir={avertir} />
            </TabsContent>
          </div>
        </Tabs>
      )}
    </div>
  );
};

export default ParametresRecus;
